<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col justify-between gap-8">
            <div class="bg-primary-150 p-4 text-[16px] text-gray-625">
                The
                <span class="font-semibold">EMTprep Readiness Score</span> is a
                measurement of your preparedness to pass the NREMT. Raise it by
                using our study materials, when your score is 90 or higher
                you're ready to take the NREMT. Good luck!
            </div>
            <div class="flex-none">
                <StudentReadinessScoreWidget
                    :score-percent="scorePercent"
                    :compact="false"
                />
            </div>
        </div>
        <div class="">
            <div class="font-bold text-gray-800">Breakdown</div>
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.SET_A_TEST_DAY"
                detail-title="Set a test Date"
                detail-description="Set a test date for your NREMT exam"
                :points-earned="scoreDetails.SET_A_TEST_DAY.points_earned"
                :points-available="scoreDetails.SET_A_TEST_DAY.points_available"
                :completed="scoreDetails.SET_A_TEST_DAY.completed"
            />
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.TAKE_ASSESSMENT_QUIZ"
                detail-title="Assessment Quiz"
                detail-description="Complete your initial 50-question assessment quiz"
                :points-earned="scoreDetails.TAKE_ASSESSMENT_QUIZ.points_earned"
                :points-available="
                    scoreDetails.TAKE_ASSESSMENT_QUIZ.points_available
                "
                :completed="scoreDetails.TAKE_ASSESSMENT_QUIZ.completed"
            />
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.QUIZZES_COMPLETED"
                detail-title="Quiz Completion"
                detail-description="Earn points based on the # of quizzes you complete."
                :points-earned="scoreDetails.QUIZZES_COMPLETED.points_earned"
                :points-available="
                    scoreDetails.QUIZZES_COMPLETED.points_available
                "
                :completed="scoreDetails.QUIZZES_COMPLETED.completed"
            >
                <StudentReadinessScoreDetailsMetricCompletionTable
                    metric-title-singular="quiz"
                    metric-title-plural="quizzes"
                    :metrics-completed="
                        $page.props.studentReadinessScore.details
                            .QUIZZES_COMPLETED.quizzes_completed
                    "
                    :table-thresholds="
                        $page.props.studentReadinessScore.thresholds
                            .QUIZZES_COMPLETED
                    "
                />
            </StudentReadinessScoreDetailRow>
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.QUIZ_PERFORMANCE"
                detail-title="Quiz Performance"
                detail-description="After you hit more than 100 questions in a category, you’ll start earning points for your average score"
                :points-earned="scoreDetails.QUIZ_PERFORMANCE.points_earned"
                :points-available="
                    scoreDetails.QUIZ_PERFORMANCE.points_available
                "
                :completed="scoreDetails.QUIZ_PERFORMANCE.completed"
            >
                <StudentReadinessScoreDetailsQuizPerformance
                    :focus-areas="scoreDetails.QUIZ_PERFORMANCE.focus_areas"
                />
            </StudentReadinessScoreDetailRow>
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.TIME_SPENT_STUDYING"
                detail-title="Time spent studying"
                detail-description="Spend non-idle time on interacting with the site"
                :points-earned="scoreDetails.TIME_SPENT_STUDYING.points_earned"
                :points-available="
                    scoreDetails.TIME_SPENT_STUDYING.points_available
                "
                :completed="scoreDetails.TIME_SPENT_STUDYING.completed"
            >
                <StudentReadinessScoreDetailsTimeSpentStudyingTable
                    :minutes="scoreDetails.TIME_SPENT_STUDYING.minutes"
                    :table-thresholds="
                        $page.props.studentReadinessScore.thresholds
                            .TIME_SPENT_STUDYING
                    "
                />
            </StudentReadinessScoreDetailRow>
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.VIDEOS_WATCHED"
                detail-title="Videos"
                detail-description="Watch videos form our catalog"
                :points-earned="scoreDetails.VIDEOS_WATCHED.points_earned"
                :points-available="scoreDetails.VIDEOS_WATCHED.points_available"
                :completed="scoreDetails.VIDEOS_WATCHED.completed"
            >
                <StudentReadinessScoreDetailsMetricCompletionTable
                    metric-title-singular="Video"
                    metric-title-plural="Videos"
                    :metrics-completed="
                        scoreDetails.VIDEOS_WATCHED.videos_watched
                    "
                    :table-thresholds="
                        $page.props.studentReadinessScore.thresholds
                            .VIDEOS_WATCHED
                    "
                />
            </StudentReadinessScoreDetailRow>
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.FLASHCARD_STACKS_COMPLETED"
                detail-title="Flashcards"
                detail-description="Complete decks of flashcards"
                :points-earned="
                    scoreDetails.FLASHCARD_STACKS_COMPLETED.points_earned
                "
                :points-available="
                    scoreDetails.FLASHCARD_STACKS_COMPLETED.points_available
                "
                :completed="scoreDetails.FLASHCARD_STACKS_COMPLETED.completed"
            >
                <StudentReadinessScoreDetailsMetricCompletionTable
                    metric-title-singular="Deck"
                    metric-title-plural="Decks"
                    :metrics-completed="
                        scoreDetails.FLASHCARD_STACKS_COMPLETED
                            .flashcard_stacks_completed
                    "
                    :table-thresholds="
                        $page.props.studentReadinessScore.thresholds
                            .FLASHCARD_STACKS_COMPLETED
                    "
                />
            </StudentReadinessScoreDetailRow>
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.STUDY_GUIDES_COMPLETED"
                detail-title="Study Guides"
                detail-description="Read study guides"
                :points-earned="scoreDetails.STUDY_GUIDES_COMPLETED.points_earned"
                :points-available="
                    scoreDetails.STUDY_GUIDES_COMPLETED.points_available
                "
                :completed="scoreDetails.STUDY_GUIDES_COMPLETED.completed"
            >
                <StudentReadinessScoreDetailsMetricCompletionTable
                    metric-title-singular="Study Guide"
                    metric-title-plural="Study Guides"
                    :metrics-completed="
                        scoreDetails.STUDY_GUIDES_COMPLETED.study_guides_opened
                    "
                    :table-thresholds="
                        $page.props.studentReadinessScore.thresholds
                            .STUDY_GUIDES_COMPLETED
                    "
                />
            </StudentReadinessScoreDetailRow>

            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.WORKSHEETS_COMPLETED"
                detail-title="Interactive Exercises"
                detail-description="Complete interactive exercises"
                :points-earned="scoreDetails.WORKSHEETS_COMPLETED.points_earned"
                :points-available="
                    scoreDetails.WORKSHEETS_COMPLETED.points_available
                "
                :completed="scoreDetails.WORKSHEETS_COMPLETED.completed"
            >
                <StudentReadinessScoreDetailsMetricCompletionTable
                    metric-title-singular="Exercise"
                    metric-title-plural="Exercises"
                    :metrics-completed="
                        scoreDetails.WORKSHEETS_COMPLETED.worksheets_completed
                    "
                    :table-thresholds="
                        $page.props.studentReadinessScore.thresholds
                            .WORKSHEETS_COMPLETED
                    "
                />
            </StudentReadinessScoreDetailRow>
            <StudentReadinessScoreDetailRow
                v-if="scoreDetails.CAT_SCORE"
                detail-title="Computer Adaptive Test (CAT)"
                detail-description="Earn points for your performance on our computer adaptive test"
                :points-earned="scoreDetails.CAT_SCORE.points_earned"
                :points-available="scoreDetails.CAT_SCORE.points_available"
                :completed="scoreDetails.CAT_SCORE.completed"
            >
                <StudentReadinessScoreDetailsCatTable
                    :points-earned="scoreDetails.CAT_SCORE.points_earned"
                />
            </StudentReadinessScoreDetailRow>
        </div>
    </div>
</template>
<script>
import StudentReadinessScoreWidget from "@/Components/StudentReadinessScoreWidget.vue";
import StudentReadinessScoreDetailRow from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailRow.vue";
import StudentReadinessScoreDetailsQuizCompletionTable
    from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsMetricCompletionTable.vue";
import StudentReadinessScoreDetailsTimeSpentStudyingTable
    from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsTimeSpentStudyingTable.vue";
import StudentReadinessScoreDetailsVideosWatchedTable
    from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsVideosWatchedTable.vue";
import StudentReadinessScoreDetailsFlashcardStacksCompletedTable
    from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsFlashcardStacksCompletedTable.vue";
import StudentReadinessScoreDetailsWorksheetsCompletedTable
    from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsWorksheetsCompletedTable.vue";
import StudentReadinessScoreDetailsCatTable from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsCatTable.vue";
import StudentReadinessScoreDetailsQuizPerformance
    from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsQuizPerformance.vue";
import StudentReadinessScoreDetailsMetricCompletionTable
    from "@/Pages/NewStudentBackend/StudentReadinessScoreDetailsMetricCompletionTable.vue";

export default {
    name: "StudentReadinessDetails",
    components: {
        StudentReadinessScoreDetailsQuizPerformance,
        StudentReadinessScoreDetailsCatTable,
        StudentReadinessScoreDetailsWorksheetsCompletedTable,
        StudentReadinessScoreDetailsFlashcardStacksCompletedTable,
        StudentReadinessScoreDetailsVideosWatchedTable,
        StudentReadinessScoreDetailsQuizCompletionTable,
        StudentReadinessScoreDetailsTimeSpentStudyingTable,
        StudentReadinessScoreDetailRow,
        StudentReadinessScoreWidget,
        StudentReadinessScoreDetailsMetricCompletionTable,
    },
    props: {
        scorePercent: {
            type: Number,
            default: 0,
        },
        scoreDetails: {
            type: Object,
            default: {},
        },
    },
};
</script>
